import { CommonModule } from '@angular/common';
import { Component, NgModule } from '@angular/core';
import { Router } from '@angular/router';
import {
  DxButtonModule,
  DxLoadIndicatorModule,
  DxScrollViewModule,
} from 'devextreme-angular';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  JobTrackerService,
  TrackedJob,
  TrackedJobStatus,
} from 'src/app/shared/services/job-tracker.service';
import { OverlayModule } from '../overlay/overlay.component';

/** Jobs tracked before `label` existed have none (job-tracker.service.ts) -- always a fallback. */
const FALLBACK_LABEL = 'Untitled job';

/**
 * The `kind` -> route/label map lives here, not on the job. A route stored on the job becomes
 * wrong when the destination moves. Code does not. Add an entry for each new job kind.
 *
 * `route` is a builder, not a fixed string. `listing-editor.routing.module.ts` has no default
 * child for `manage-listings`, only `:catalog_type` and `details/:id`. Thus the bare
 * `/manage-listings` matches nothing and shows a blank page. The segment comes from
 * `job.context`: the `catalog_type` of the upload, which the uploader sets when it calls
 * `track()`. Jobs tracked before `context` have none. Return `null`, do not guess a catalog.
 * This gives the same result as an unconfigured kind.
 *
 * The `openUpload` query param opens the results, not only the page. `getQueryParams()` in
 * `manage-listings.component.ts` opens the upload popup (`showListingUpload`) when it finds
 * this param. It opens the "add listing" wizard for `?ndc=` in the same way. `onItemClick`
 * uses `navigateByUrl`, not `navigate`, so the router reads this query string as a query
 * string and does not encode it into the path.
 *
 * The value is the id of the clicked job, not a bare `1`. Only the id tells the uploader which
 * job the user clicked. Without the id, the uploader uses the most recent job of the kind. With
 * two tracked imports, a click on the older job then opens that catalog but shows the result of
 * the newer job, and Apply applies the wrong file. Thus the builder reads the full job, not
 * only `context`.
 */
const JOB_KIND_CONFIG: Readonly<
  Record<
    string,
    {
      readonly label: string;
      readonly route: (job: TrackedJob) => string | null;
    }
  >
> = {
  catalog_import: {
    label: 'Catalog import',
    route: job =>
      job.context
        ? `/manage-listings/${job.context}?openUpload=${job.id}`
        : null,
  },
};

const STATUS_LABELS: Readonly<Record<TrackedJobStatus, string>> = {
  queued: 'Queued',
  running: 'In progress',
  success: 'Complete',
  error: 'Failed',
  gone: 'No longer available',
  unreachable: 'Status unknown',
};

const ACTIVE_STATUSES: ReadonlyArray<TrackedJobStatus> = ['queued', 'running'];

type IndicatorState =
  | { readonly kind: 'hidden' }
  | { readonly kind: 'active' }
  /** `count` is the number of UNSEEN terminal jobs, not the total. See deriveIndicatorState.
   *  It can be 0 while jobs are still present: all seen, none dismissed. The template then
   *  hides the badge but keeps the bell, so the panel stays available. */
  | { readonly kind: 'done'; readonly count: number };

interface JobPanelItem {
  readonly id: string;
  readonly label: string;
  readonly kindLabel: string;
  readonly statusLabel: string;
  /** Only set for error/gone/unreachable -- see statusDetailOf. */
  readonly statusDetail: string | null;
  readonly startedAt: Date | null;
  readonly route: string | null;
}

/**
 * The badge counts unseen terminal jobs, not all jobs. A click on a job (onItemClick) marks it
 * seen but does not remove it. Thus the count decreases and the job stays available in the
 * panel. Seen and dismissed are different actions.
 */
function deriveIndicatorState(jobs: readonly TrackedJob[]): IndicatorState {
  if (!jobs.length) {
    return { kind: 'hidden' };
  }
  const hasActiveJob = jobs.some(job => ACTIVE_STATUSES.includes(job.status));
  if (hasActiveJob) {
    return { kind: 'active' };
  }
  return { kind: 'done', count: jobs.filter(job => !job.seen).length };
}

/**
 * Only `error`, `gone` and `unreachable` need an explanation. The service writes the correct
 * text for `gone` and for `unreachable` (job-tracker.service.ts). Use `job.error` here. A
 * second version of the text would give two sources of truth.
 */
function statusDetailOf(job: TrackedJob): string | null {
  const hasDetail =
    job.status === 'error' ||
    job.status === 'gone' ||
    job.status === 'unreachable';
  return hasDetail ? (job.error ?? null) : null;
}

/** Fallback for a `kind` with no entry in JOB_KIND_CONFIG yet: `foo_bar` -> `foo bar`. */
function humanizeKind(kind: string): string {
  const words = kind.split('_').filter(Boolean);
  return words.length ? words.join(' ') : kind;
}

function toPanelItem(job: TrackedJob): JobPanelItem {
  const config = JOB_KIND_CONFIG[job.kind];
  return {
    id: job.id,
    label: job.label ?? FALLBACK_LABEL,
    kindLabel: config?.label ?? humanizeKind(job.kind),
    statusLabel: STATUS_LABELS[job.status],
    statusDetail: statusDetailOf(job),
    startedAt: job.createdAt ? new Date(job.createdAt) : null,
    route: config?.route(job) ?? null,
  };
}

/**
 * The header view of JobTrackerService.jobs$. It shows a spinner while a tracked job runs, and
 * a badge that the user can dismiss when a job finishes. It reads jobs$ directly. Each tracked
 * job is one panel item, and each item names its own id in its route. Thus no code here or
 * downstream must reduce the concurrent jobs of one kind to the most recent job.
 */
@Component({
  selector: 'app-job-tracker-indicator',
  templateUrl: './job-tracker-indicator.component.html',
  styleUrls: ['./job-tracker-indicator.component.scss'],
})
export class JobTrackerIndicatorComponent {
  readonly indicatorState$: Observable<IndicatorState>;
  readonly panelItems$: Observable<JobPanelItem[]>;

  isPanelOpen = false;

  constructor(
    private readonly jobTrackerService: JobTrackerService,
    private readonly router: Router,
  ) {
    this.indicatorState$ = this.jobTrackerService.jobs$.pipe(
      map(deriveIndicatorState),
    );
    this.panelItems$ = this.jobTrackerService.jobs$.pipe(
      map(jobs => jobs.map(toPanelItem)),
    );
  }

  togglePanel(): void {
    this.isPanelOpen = !this.isPanelOpen;
  }

  closePanel(): void {
    this.isPanelOpen = false;
  }

  /**
   * Always mark the job seen, because the user looked at it. This applies also when there is no
   * route to open (see JOB_KIND_CONFIG). To open the panel does not mark a job seen. The badge
   * is what attracts the eye. If an open cleared the badge before the user acts on one job, the
   * badge could no longer answer the question that it exists to answer.
   *
   * Use `navigateByUrl`, not `navigate([item.route])`. `route` contains a query string
   * (`?openUpload=<jobId>`). `navigate` with one array element reads `?` as a path character,
   * not as a query separator. `navigateByUrl` parses the URL like the address bar.
   */
  onItemClick(item: JobPanelItem): void {
    this.jobTrackerService.markSeen(item.id);
    if (!item.route) {
      return;
    }
    this.isPanelOpen = false;
    this.router.navigateByUrl(item.route);
  }

  onDismiss(jobId: string): void {
    this.jobTrackerService.dismiss(jobId);
  }

  trackById(_index: number, item: JobPanelItem): string {
    return item.id;
  }
}

@NgModule({
  imports: [
    CommonModule,
    DxButtonModule,
    DxLoadIndicatorModule,
    DxScrollViewModule,
    OverlayModule,
  ],
  declarations: [JobTrackerIndicatorComponent],
  exports: [JobTrackerIndicatorComponent],
})
export class JobTrackerIndicatorModule {}
